import React, { useState, useEffect, useMemo } from "react";
import loader from "../../../assets/Images/loader.gif";
import { useNavigate } from "react-router-dom";

/* ---------- Helpers ---------- */
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = (n) => String(n).padStart(2, "0");

const toDateObj = (str) => {
  if (!str) return null;
  const d = new Date(str.replace(" ", "T"));
  return isNaN(d) ? null : d;
};

const formatDate = (dt) => `${pad2(dt.getDate())}-${MONTH_SHORT[dt.getMonth()]}`;
const formatTime = (dt) => {
  let h = dt.getHours();
  const m = pad2(dt.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

const formatRange = (startStr, endStr) => {
  const s = toDateObj(startStr);
  const e = toDateObj(endStr);
  if (!s || !e) return { date: "", range: "", duration: "" };
  const sameDay = s.toDateString() === e.toDateString();
  const date = sameDay ? formatDate(s) : `${formatDate(s)} → ${formatDate(e)}`;
  const range = `${formatTime(s)} to ${formatTime(e)}`;
  const durationMin = Math.round((e - s) / 60000);
  return { date, range, duration: `${durationMin} Min` };
};

const statusBg = (statusRaw) => {
  const status = (statusRaw || "").trim().toLowerCase();
  if (status === "open") return "#ffe6e6";         // light red
  if (status === "completed") return "#e6ffe6";     // light green
  if (status === "canceled" || status === "cancelled") return "#fff5e6"; // light orange
  return "#ffffff";
};

const norm = (s) => (s || "").trim().toLowerCase();

const attachKeys = (list) => {
  const used = new Set();
  return list.map((i, idx) => {
    const base =
      i.MeetingID ??
      `${i.ContactID || "NA"}_${i.MeetingDateandTime || "NA"}`;

    let key = base;
    let n = 1;
    while (used.has(key)) key = `${base}__${n++}`;
    used.add(key);

    return { ...i, __id: key };
  });
};

/* ---------- Component ---------- */
const BusinessMraAllMeetings = () => {
  const [activeTab, setActiveTab] = useState("open");
  const [loading, setLoading] = useState(true);
  const [contactData, setContactData] = useState([]);

  const today = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
  }, []);
  const [selectedDate, setSelectedDate] = useState(today);

  useEffect(() => {
    const now = new Date();
    const toDate = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

    const fromDateObj = new Date(now);
    fromDateObj.setMonth(now.getMonth() - 2);
    const formattedFromDate = `${fromDateObj.getFullYear()}-${pad2(fromDateObj.getMonth() + 1)}-${pad2(fromDateObj.getDate())}`;

    const fetchMeetings = async () => {
      setLoading(true);
      try {
        const requestData = {
          Authkey: "ac7b51de9d888e1458dd53d8aJAN3ba6f",
          Action: "geAlltMeeting", // <-- ensure correct action string
          FromDate: formattedFromDate,
          ToDate: toDate,
          CurrentDate: "Yes",
          password: sessionStorage.getItem("password"),
          userName: sessionStorage.getItem("userName"),
        };

        const response = await fetch(process.env.REACT_APP_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });

        const data = await response.json();
        if (data.Status === "Success") {
          const list = (data.Response?.Data?.ContactData) || [];
          setContactData(attachKeys(list));
        } else {
          console.error("API Error:", data);
        }
      } catch (err) {
        console.error("Error fetching meetings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMeetings();
  }, []);

  const navigate = useNavigate();
  const redirectBusinessPage = () => navigate("/businessMraCardList");

  const renderCard = (item) => {
    const { date, range, duration } = formatRange(item.MeetingDateandTime, item.MeetingEndDateandTime);
    return (
      <div className="col" key={item.__id}>
        <div className="card mt-3" style={{ backgroundColor: statusBg(item.MeetingStatus) }}>
          <div className="m-2 card-data">
            <p className="opportunity-card-title">{item.NameOfCompany}</p>
            <p className="opportunity-card-title">{item.ContactID}</p>
            <p className="opportunity-card-text">Date: {date}</p>
            <p className="opportunity-card-text">Time: {range}</p>
            <p className="opportunity-card-text">Duration: {duration}</p>
            <p className="opportunity-card-text">Mode: {item.MeetingMode || "N/A"}</p>
            <p className="opportunity-card-text">Status: {item.MeetingStatus || "N/A"}</p>
            <p className="opportunity-card-text">Met With: {item.MetWithWhom || "N/A"}</p>
            <p className="opportunity-card-text">Remarks: {item.Remarks || "N/A"}</p>
          </div>
        </div>
      </div>
    );
  };

  const filteredData = useMemo(() => {
    return contactData.filter((item) => {
      const sDate = toDateObj(item.MeetingDateandTime);
      switch (activeTab) {
        case "today": {
          if (!sDate) return false;
          const sel = new Date(selectedDate);
          return sDate.toDateString() === sel.toDateString();
        }
        case "open":
          return norm(item.MeetingStatus) === "open";
        case "completed":
          return norm(item.MeetingStatus) === "completed";
        case "cancelled":
          return ["canceled", "cancelled"].includes(norm(item.MeetingStatus));
        default:
          return false;
      }
    });
  }, [contactData, activeTab, selectedDate]);

  const Loader = () => (
    <div className="loader-wrapper">
      <div className="loader">
        <img src={loader} alt="loading..." />
      </div>
    </div>
  );

  return (
    <>
      {/* Header */}
      <div className="container-fluid text-light Header-style header">
        <div className="p-3">
          <span className="p-1 m-3 h4">Appointments</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="container-fluid text-light Header-style header">
        <div className="p-3">
          <span style={{ cursor: "pointer" }} onClick={redirectBusinessPage}>
            <i className="fa-solid fa-arrow-left-long" />
          </span>
          <span className="p-1 m-3 h4">Meeting Details</span>
        </div>
        <ul className="nav nav-tabs">
          {[
            { key: "open", label: "Open" },
            { key: "completed", label: "Completed" },
            { key: "cancelled", label: "Cancelled" },
            { key: "today", label: "Scheduled Meetings" },
          ].map((tab) => (
            <li className="nav-item" key={tab.key}>
              <a
                className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab(tab.key);
                }}
              >
                {tab.label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Content */}
      <div className="container-fluid bg-white py-3" style={{ marginTop: "80px" }}>
        {activeTab === "today" && (
          <div className="mb-3">
            <label htmlFor="meetingDate" className="form-label">
              Select date:
            </label>
            <input
              type="date"
              id="meetingDate"
              className="form-control form-control-sm"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        )}

        <div className="row row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-3">
          {loading ? (
            <Loader />
          ) : filteredData.length > 0 ? (
            filteredData.map(renderCard)
          ) : (
            <p className="text-center mt-3 text-muted">
              {activeTab === "today"
                ? `No meetings scheduled for ${selectedDate}`
                : `No ${activeTab} meetings found`}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default BusinessMraAllMeetings;
